import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit
} from '@angular/core';
import { Chart } from 'angular-highcharts';
import { ElementSettings } from '../../../models/models-index';
import * as Models from '../../../models/models-index';
import {
  compositeFunnelKpiPieChartPlaceholder,
  funnelChartPlaceholder
} from '../../../constants/highcharts-placeholders';
import { LoggerService } from '../../../services/services-index';
import { getChartColors } from '../panel-utils';
import { funnelSettings } from '../../../constants/constants';

interface MetricData {
  title: string;
  value?: any;
  trend?: any;
  trendLabel?: any;
  isComposite?: boolean;
}

@Component({
  selector: 'kpi-vertical-funnel',
  standalone: false,
  templateUrl: './kpi-vertical-funnel.component.html',
  styleUrls: ['../panel-elements.scss', './kpi-vertical-funnel.component.scss']
})
export class KpiVerticalFunnelComponent
  implements Models.ElementComponent, OnInit, AfterViewInit, OnDestroy {
  @Input() settings: ElementSettings;

  @Input() set dataSets(value: Models.DataSet[]) {
    const hasData = !!(value && value.length > 0);
    console.log(`KPI Vertical Funnel - DataSets input changed, has data: ${hasData}`);
    this.initializePlaceholder();

    if (hasData) {
      try {
        console.log('KPI Vertical Funnel - Processing data sets synchronously');
        this.processFunnelData(value[0]);
        this.processMetricsData(value[0]);
        if (value.length > 1) this.processPieChartData(value[1]);

        this.isDataSetLoaded = true;
        this.containerWidthInitialized = true;

        Promise.resolve().then(() => {
          this.updateMetricsVisibility();
          this.reflowFunnelChart();
          requestAnimationFrame(() => {
            this.updateMetricsVisibility();
            this.reflowFunnelChart();
          });
        });
      } catch (error) {
        console.error('KPI Vertical Funnel - Error processing data sets:', error);
        this.isDataSetLoaded = false;
      }
    } else {
      console.log('KPI Vertical Funnel - No data sets loaded, using placeholder');
      this.isDataSetLoaded = false;
      this.containerWidthInitialized = true;

      Promise.resolve().then(() => {
        this.reflowPlaceholderFunnelChart();
        this.updateMetricsVisibility();
      });
    }
  }

  isDataSetLoaded = false;
  componentActive = true;
  containerWidthInitialized = false;

  funnelChart: Chart;
  funnelPlaceholderChart: Chart;
  pieChart: Chart;
  piePlaceholderChart: Chart;

  rightSideMetrics: MetricData[] = [];
  placeholderMetrics: MetricData[] = [
    { title: 'Metric 1' },
    { title: 'Metric 2' },
    { title: 'Metric 3' },
    { title: 'Metric 4' },
    { title: 'Metric 5' },
    { title: 'Metric 6', isComposite: true }
  ];

  private resizeObserver: ResizeObserver;
  private metricsGridElement: HTMLElement | null = null;

  constructor(
    private loggerService: LoggerService,
    private elementRef: ElementRef
  ) {}

  ngOnInit() {
    this.initializePlaceholder();
    this.containerWidthInitialized = true;
    this.setupResizeHandling();
    Promise.resolve().then(() => this.updateMetricsVisibility());
  }

  ngAfterViewInit() {
    console.log('KPI Vertical Funnel - ngAfterViewInit started');
    this.metricsGridElement = this.elementRef.nativeElement.querySelector('.metrics-grid');
    this.updateMetricsVisibility();

    requestAnimationFrame(() => {
      this.containerWidthInitialized = true;
      console.log(`KPI Vertical Funnel - Container width in ngAfterViewInit RAF: ${this.elementRef.nativeElement.offsetWidth}px`);

      if (!this.metricsGridElement) {
        this.metricsGridElement = this.elementRef.nativeElement.querySelector('.metrics-grid');
        console.log('KPI Vertical Funnel - Metrics grid element check in RAF:', this.metricsGridElement ? 'FOUND' : 'NOT FOUND');
      }

      this.updateMetricsVisibility();
      this.isDataSetLoaded ? this.reflowFunnelChart() : this.reflowPlaceholderFunnelChart();
    });
  }

  ngOnDestroy() {
    console.log('KPI Vertical Funnel - Component destroying, cleaning up resources');
    this.componentActive = false;
    if (this.resizeObserver) {
      try {
        this.resizeObserver.disconnect();
        console.log('KPI Vertical Funnel - ResizeObserver disconnected');
      } catch (error) {
        console.log('KPI Vertical Funnel - Error disconnecting ResizeObserver:', error);
      }
    }
    this.metricsGridElement = null;
  }

  private setupResizeHandling(): void {
    try {
      let resizeDebounceTimer: number;
      this.resizeObserver = new ResizeObserver(entries => {
        if (resizeDebounceTimer) clearTimeout(resizeDebounceTimer);

        if (entries && entries[0]) {
          const width = entries[0].contentRect.width;
          //console.log(`KPI Vertical Funnel - Container width from ResizeObserver: ${width}px`);
        }

        this.updateMetricsVisibility();

        resizeDebounceTimer = window.setTimeout(() => {
          if (this.componentActive) this.updateMetricsVisibility();
        }, 50);
      });

      if (this.elementRef?.nativeElement) {
        this.resizeObserver.observe(this.elementRef.nativeElement);
        console.log('KPI Vertical Funnel - ResizeObserver attached successfully');
      } else {
        this.setupWindowResizeFallback();
      }
    } catch (error) {
      console.log('KPI Vertical Funnel - ERROR setting up ResizeObserver:', error);
      this.setupWindowResizeFallback();
    }
  }

  private setupWindowResizeFallback(): void {
    console.log('KPI Vertical Funnel - Setting up window resize listener fallback');
    let resizeTimer: number;
    const handleResize = () => {
      if (!this.componentActive) return;
      if (resizeTimer) clearTimeout(resizeTimer);

      this.updateMetricsVisibility();

      resizeTimer = window.setTimeout(() => {
        if (this.componentActive) this.updateMetricsVisibility();
      }, 100);
    };

    window.addEventListener('resize', handleResize);
  }

  getTrendArrowIconClass(metric: MetricData): string {
    if (metric.trend == null || metric.trend === 0) return 'fa fa-horizontal-rule';
    return metric.trend > 0
      ? 'fa fa-chevron-circle-up chevron-icon'
      : 'fa fa-chevron-circle-down chevron-icon';
  }

  getTrendIconColor(metric: MetricData): string {
    if (metric.trend == null) return 'bg-yellow';
    return metric.trend > 0 ? 'bg-green' : 'bg-red';
  }

  shouldShowMetrics(): boolean {
    const metricsEnabled = this.settings?.options?.['showMetrics'] === 'true';
    if (!metricsEnabled) return false;

    if (this.containerWidthInitialized && this.elementRef?.nativeElement) {
      const containerWidth = this.elementRef.nativeElement.offsetWidth;
      return containerWidth >= 1000;
    }

    return metricsEnabled;
  }

  private useLogarithmicScale(): boolean {
    return this.settings?.options?.[funnelSettings.scaleOption] === funnelSettings.logarithmicScaleValue;
  }

  private initializePlaceholder(): void {
    console.log('KPI Vertical Funnel - Initializing placeholder charts');
    try {
      this.funnelPlaceholderChart = new Chart(funnelChartPlaceholder);
      this.piePlaceholderChart = new Chart(compositeFunnelKpiPieChartPlaceholder);

      if (this.containerWidthInitialized) {
        Promise.resolve().then(() => {
          this.funnelPlaceholderChart?.ref?.reflow();
          this.piePlaceholderChart?.ref?.reflow();
        });
      }
    } catch (error) {
      console.error('KPI Vertical Funnel - Error initializing placeholder charts:', error);
    }
  }

  private processFunnelData(dataSet: Models.DataSet): void {
    const metricNames = this.settings.options[funnelSettings.funnelMetricsOption].split(',');
    const funnelSeriesData: any[] = [];

    for (const name of metricNames) {
      const metric = this.settings.metrics.find(m => m.name === name);
      if (!metric) {
        this.loggerService.logError(`Funnel metric config error: metric definition for '${name}' not found.`);
        this.isDataSetLoaded = false;
        return;
      }

      const idx = dataSet.columns.findIndex(c => c.name === name);
      if (idx < 0) {
        this.loggerService.logError(`Funnel metric column '${name}' not found in dataset.`);
        this.isDataSetLoaded = false;
        return;
      }

      let value = +dataSet.rows[0][idx]?.value;
      if (this.useLogarithmicScale() && value > 0) value = Math.log10(value);
      const label = dataSet.rows[0][idx]?.label ?? name;

      funnelSeriesData.push({
        name: metric.title || name,
        y: value,
        label,
        displayValue: label
      });
    }

    this.funnelChart = this.generateFunnelChart(funnelSeriesData);
  }

  private processMetricsData(dataSet: Models.DataSet): void {
    const metricNames = this.settings.options[funnelSettings.rightSideMetricsOption].split(',');
    const sideMetrics: MetricData[] = [];

    for (const name of metricNames) {
      const metric = this.settings.metrics.find(m => m.name === name);
      if (!metric) {
        this.loggerService.logError(`Side metric config error: metric definition for '${name}' not found.`);
        this.isDataSetLoaded = false;
        return;
      }

      const idx = dataSet.columns.findIndex(c => c.name === name);
      if (idx < 0) {
        this.loggerService.logError(`Side metric column '${name}' not found in dataset.`);
        this.isDataSetLoaded = false;
        return;
      }

      const prevIdx = dataSet.columns.findIndex(c => c.name === metric.previousPeriod);
      const metricPOPIndex = dataSet.columns.findIndex(c => c.name === metric.pop);

      const cell = dataSet.rows[0][idx];
      const prevCell = dataSet.rows[0][prevIdx];
      const metricPOP = dataSet.rows[0][metricPOPIndex];

      sideMetrics.push({
        title: metric.title || name,
        value: cell?.label ?? cell?.value,
        trend: metricPOP,
        trendLabel: prevCell?.label,
        isComposite: false
      });
    }

    sideMetrics.push({
      title: this.settings.options[funnelSettings.pieChartMetricTitleOption] || funnelSettings.pieChartDefaultPlaceholderTitle,
      isComposite: true
    });

    this.rightSideMetrics = sideMetrics;
  }

  private processPieChartData(dataSet: Models.DataSet): void {
    const metricName = this.settings.options[funnelSettings.pieChartMetricNameOption];
    const dimIndex = dataSet.columns.findIndex(col => col.name === this.settings.dimensionName);
    const metricIndex = dataSet.columns.findIndex(col => col.name === metricName);

    if (metricIndex === -1) {
      this.loggerService.logError(`Pie chart metric '${metricName}' not found in dataset.`);
      this.isDataSetLoaded = false;
      return;
    }

    if (dimIndex === -1) {
      this.loggerService.logError(`Dimension column '${this.settings.dimensionName}' not found in dataset.`);
      this.isDataSetLoaded = false;
      return;
    }

    let seriesData = [];
    let rows = [...dataSet.rows];
    rows.sort((a, b) => (b[metricIndex].value as number) - (a[metricIndex].value as number));

    for (let i = 0; i < rows.length; i++) {
      const dimVal = rows[i][dimIndex];
      const metricVal = rows[i][metricIndex];

      if (!dimVal?.value || dimVal.label === '' || !metricVal?.value) {
        this.loggerService.logError(`Pie chart row ${i} skipped due to missing or invalid data.`);
        continue;
      }

      if (this.settings.dimensionRowFilters?.some(f => f === dimVal.value)) {
        this.loggerService.log(`Pie chart row ${i} skipped: filtered dimension '${dimVal.value}'.`);
        continue;
      }

      seriesData.push({
        name: dimVal.label ?? dimVal.value,
        displayValue: metricVal.label ?? metricVal.value,
        y: metricVal.value
      });
    }

    this.pieChart = this.generatePieChart(seriesData);
  }

  private generateFunnelChart(data: any[]): Chart {
    const colors = getChartColors(this.settings);
    return new Chart({
      credits: { enabled: false },
      title: null,
      chart: { type: 'funnel', animation: null },
      colors,
      plotOptions: {
        series: {
          center: ['40%', '50%'],
          neckWidth: '30%',
          neckHeight: '25%',
          width: '75%',
          dataLabels: {
            enabled: true,
            softConnector: true,
            crop: false,
            style: {
              fontWeight: 'bold',
              color: 'black',
              textOutline: 'none'
            },
            formatter: function () {
              const point = this.point as any;
              return `<b>${point.name}</b>: ${point.displayValue}`;
            }
          }
        } as any
      },
      tooltip: {
        headerFormat: '',
        pointFormatter: function () {
          const point = this as any;
          return `<b>${point.name}: ${point.displayValue}</b>`;
        }
      },
      legend: { enabled: false },
      series: [{ type: 'funnel', data }],
      responsive: {
        rules: [{
          condition: { maxWidth: 500 },
          chartOptions: {
            plotOptions: {
              series: {
                center: ['50%', '50%'],
                width: '100%',
                dataLabels: { inside: true, softConnector: true }
              } as any
            }
          }
        }]
      }
    } as Highcharts.Options);
  }

  private generatePieChart(data: any[]): Chart {
    const colors = getChartColors(this.settings);
    return new Chart({
      chart: { type: 'pie', animation: null },
      colors,
      title: null,
      credits: { enabled: false },
      plotOptions: {
        pie: {
          size: '155%',
          center: ['50%', '50%'],
          dataLabels: {
            enabled: false,
            connectorShape: 'fixed',
            softConnector: false
          }
        }
      },
      tooltip: {
        headerFormat: '',
        pointFormatter: function () {
          const point = this as any;
          return `<b>${point.name}: ${point.displayValue}</b>`;
        }
      },
      series: [{ type: 'pie', data }]
    });
  }

  private reflowFunnelChart(): void {
    if (!this.componentActive) return;
    try {
      this.funnelChart?.ref?.reflow();
      this.pieChart?.ref?.reflow();
    } catch (error) {
      console.error('KPI Vertical Funnel - Error reflowing chart:', error);
    }
    void this.elementRef.nativeElement.offsetHeight;
  }

  private reflowPlaceholderFunnelChart(): void {
    if (!this.componentActive) return;
    try {
      this.funnelPlaceholderChart?.ref?.reflow();
      this.piePlaceholderChart?.ref?.reflow();
    } catch (error) {
      console.error('KPI Vertical Funnel - Error reflowing placeholder chart:', error);
    }
    void this.elementRef.nativeElement.offsetHeight;
  }

  private updateMetricsVisibility(): void {
    const container = this.elementRef.nativeElement.querySelector('.vertical-funnel-element');
    const metricsGridElements = this.elementRef.nativeElement.querySelectorAll('.metrics-grid');
    if (!container) return;

    const showMetrics = this.shouldShowMetrics();
    const containerWidth = this.elementRef.nativeElement.offsetWidth;

    container.classList.toggle('with-metrics', showMetrics);
    container.classList.toggle('no-metrics', !showMetrics);

    if (!metricsGridElements.length) {
      requestAnimationFrame(() => this.retryMetricsVisibility());
      return;
    }

    metricsGridElements.forEach(gridElement => {
      gridElement.classList.toggle('hide-metrics', !showMetrics);
      gridElement.classList.remove('hide-col-2', 'hide-col-3');

      if (showMetrics) {
        const funnelMinWidth = 700;
        const columnMinWidth = 280;
        const gapSize = 20;
        const availableWidth = containerWidth - funnelMinWidth - 40;

        if (availableWidth < (2 * columnMinWidth + gapSize)) {
          gridElement.classList.add('hide-col-2', 'hide-col-3');
        } else if (availableWidth < (3 * columnMinWidth + 2 * gapSize)) {
          gridElement.classList.add('hide-col-3');
        }
      }

      void gridElement.offsetHeight;
    });

    this.metricsGridElement = this.elementRef.nativeElement.querySelector('.metrics-grid');
    this.isDataSetLoaded
      ? this.reflowFunnelChart()
      : this.reflowPlaceholderFunnelChart();
  }

  private retryMetricsVisibility(): void {
    const retryElements = this.elementRef.nativeElement.querySelectorAll('.metrics-grid');
    const showMetrics = this.shouldShowMetrics();

    retryElements.forEach(gridElement => {
      gridElement.classList.toggle('hide-metrics', !showMetrics);
      void gridElement.offsetHeight;
    });
  }
}
